import * as Sentry from '@sentry/react';
import React from 'react';
import {
  matchRoutes,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  createBrowserRouter,
} from 'react-router-dom';
import { isDevelopment } from '../utils';

export interface ErrorContext {
  tags?: Record<string, string>;
  extras?: Record<string, unknown>;
  fingerprint?: string[];
}

class AbstractMonitoring {
  static get createBrowserRouter(): typeof createBrowserRouter {
    throw new Error('No implementation');
  }

  static init(): void {
    throw new Error('No implementation');
  }

  static setUser(_user: Sentry.User): void {
    throw new Error('No implementation');
  }

  static captureError(_error: unknown, _context?: ErrorContext): string {
    throw new Error('No implementation');
  }

  static captureSlowRequest(_functionName: string, _duration: number): void {
    throw new Error('No implementation');
  }

  static showReportDialog(): void {
    throw new Error('No implementation');
  }
}

class NoMonitoring extends AbstractMonitoring {
  static get createBrowserRouter() {
    return createBrowserRouter;
  }

  static init() {}

  static captureError(error: unknown, context?: ErrorContext): string {
    console.error('Unexpected error', error, context);

    return '';
  }

  static captureSlowRequest(functionName: string, duration: number): void {
    console.warn(`Slow request: ${functionName} took ${duration}ms`);
  }

  static setUser(_user: Sentry.User): void {}

  static showReportDialog(): void {}
}

class SentryMonitoring extends AbstractMonitoring {
  private static _createBrowserRouter = createBrowserRouter;

  static init() {
    Sentry.init({
      dsn: 'https://fc39779058c3095e9edad44366b5405c@o4508379244199936.ingest.us.sentry.io/4508381801086976',
      integrations: [
        Sentry.replayIntegration(),
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.feedbackIntegration({
          colorScheme: 'light',
          autoInject: false,
          submitButtonLabel: 'Send Feedback',
          showName: false,
          formTitle: 'Send Feedback',
          messagePlaceholder: 'How can we make Campaignswell better for you?',
          useSentryUser: {
            email: 'email',
          },
        }),
      ],
      // Session Replay
      replaysSessionSampleRate: 0.01,
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for tracing.
      // Learn more at
      // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
      tracesSampleRate: 1.0,

      // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
      tracePropagationTargets: [
        /^\//,
        /^https:\/\/bchwgeknneznkmkwpikf\.supabase\.co\/functions\/v1/,
        /^https:\/\/tgazokkrehcqqogrjowf\.supabase\.co\/functions\/v1/,
      ],
    });
    SentryMonitoring._createBrowserRouter =
      Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);
  }

  static get createBrowserRouter() {
    return SentryMonitoring._createBrowserRouter;
  }

  static setUser(user: Sentry.User): void {
    Sentry.setUser(user);
  }

  static captureError(error: unknown, context?: ErrorContext): string {
    if (!context) {
      return Sentry.captureException(error);
    }

    let eventId = '';

    Sentry.withScope((scope) => {
      if (context.tags) {
        scope.setTags(context.tags);
      }

      if (context.extras) {
        scope.setExtras(context.extras);
      }

      if (context.fingerprint) {
        scope.setFingerprint(context.fingerprint);
      }

      eventId = Sentry.captureException(error);
    });

    return eventId;
  }

  static captureSlowRequest(functionName: string, duration: number): void {
    Sentry.withScope((scope) => {
      scope.setTag('slow_request', true);
      scope.setExtras({ functionName, duration });
      Sentry.captureMessage(
        `Slow request: ${functionName} took ${duration}ms`,
        'warning',
      );
    });
  }

  static showReportDialog(): void {
    const feedback = Sentry.getFeedback();

    if (!feedback) {
      return;
    }

    feedback.createForm().then((form) => {
      form.appendToDom();
      form.open();
    });
  }
}

export const Monitoring = isDevelopment() ? NoMonitoring : SentryMonitoring;
